import { SmartRenderer } from '@fusioncharts/core/src/component-interface';
import { polarToCartesian, deg2Rad, rad2Deg } from '@fusioncharts/utils/src/scale-utils/polar-util';
import { normaliseAngle, M, A, L, Z, getArcLength, getTextRotationByQuadrant, isAngleInLeftHemisphere, getCartesianDistance, isAngleInTopHemisphere, SMALLSQUARE, MEDIUMSQUARE } from '../../chart/chord/utils';
import { getSuggestiveRotation, isIpad, convertColor, POINTER, BLANKSTRING, MIDDLESTR, TEXTANCHOR, setLineHeight, toPrecision, isIE11, UNDEF, parseTooltext } from '@fusioncharts/core/src/lib';
import { getQuadrant, getCleanAngle } from '@fusioncharts/charts/src/dataset/pie2d/pie-label-utils';

const nodeGenerator = (_startAngle, _endAngle, arc, outerRadius, innerRadius) => {
    let startAngle = normaliseAngle(_startAngle),
      endAngle = normaliseAngle(_endAngle),
      { x: x1, y: y1 } = polarToCartesian(outerRadius, startAngle, false),
      { x: x2, y: y2 } = polarToCartesian(outerRadius, endAngle, false),
      { x: x3, y: y3 } = polarToCartesian(innerRadius, startAngle, false),
      { x: x4, y: y4 } = polarToCartesian(innerRadius, endAngle, false);

    x1 = toPrecision(x1, 4);
    y1 = toPrecision(y1, 4);
    x2 = toPrecision(x2, 4);
    y2 = toPrecision(y2, 4);
    x3 = toPrecision(x3, 4);
    y3 = toPrecision(y3, 4);
    x4 = toPrecision(x4, 4);
    y4 = toPrecision(y4, 4);

    return [
      M, x1, y1,
      A, outerRadius, outerRadius, 0, arc > 180 ? 1 : 0, 1, x2, y2,
      L, x4, y4,
      A, innerRadius, innerRadius, 0, arc > 180 ? 1 : 0, 0, x3, y3,
      Z
    ];
  },
  labelPathGenerator = (_startAngle, _endAngle, arc, radius) => {
    const { x: x1, y: y1 } = polarToCartesian(
        radius,
        normaliseAngle(_startAngle),
        false
      ),
      { x: x2, y: y2 } = polarToCartesian(
        radius,
        normaliseAngle(_endAngle),
        false
      );

    return `M ${x1} ${y1} A ${radius} ${radius} ${0} ${arc > 180 ? 1 : 0} ${
      _startAngle > _endAngle ? 0 : 1
    } ${x2} ${y2}`;
  },
  RED = '#ff0000',
  LABEL_POSITION_TANGENTIAL = 'tangential',
  LABEL_POSITION_OUTSIDE = 'outside',
  LABEL_POSITION_INSIDE = 'inside',
  START = 'start',
  PADDING = 4,
  clickFn = function (e) {
    const node = this,
      manager = node.getLinkedParent(),
      legend = node.getFromEnv('legend'),
      chart = node.getFromEnv('chart'),
      legendItem = legend ? legend.getItem(manager.config.legendItemMap[node.config.label]) : null;
    let eventArgs = {};
    setEventArgs(eventArgs, node);
    manager.nodeClicked(node.config.label, legendItem);
    chart.plotEventHandler(null, e, 'dataplotclick', eventArgs);// event args need to be passed
  },
  END = 'end',
  mouseOverFn = function (e) {
    const node = this,
      chart = node.getFromEnv('chart');
    this.getLinkedParent().nodeHoverIn(this.config.label);
    let eventArgs = {};
    setEventArgs(eventArgs, node);
    chart.plotEventHandler(null, e, 'DataPlotRollOver', eventArgs);
  },
  mouseOutFn = function (e) {
    const node = this,
      chart = node.getFromEnv('chart');
    this.getLinkedParent().nodeHoverOut(this.config.label);
    let eventArgs = {};
    setEventArgs(eventArgs, node);
    chart.plotEventHandler(null, e, 'DataPlotRollOut', eventArgs);
  },
  setEventArgs = function (eventArgs, node) {
    // RED-8927: Support for plot click,label click and link click  events for Chord Diagram
    // sets the Arguments for mouse click/over/out events.
    let manager = node.getLinkedParent();
    eventArgs.label = node.config.label;
    eventArgs.color = node.config.color;
    eventArgs.alpha = node.config.alpha;
    eventArgs.links = {};
    eventArgs.value = node.config.total;
    eventArgs.displayValue = node.config.formattedValue;
    eventArgs.nodeValue = node.config.nodevalue;
    eventArgs.nodeDataValue = node.config.nodedatavalue;
    eventArgs.plotFillColor = node.config.plotfillcolor;
    eventArgs.toolText = node.config.tooltext;
    var i;
    // RED-8927: for loop is used to get link values for the current node.

    for (i = 0; i < node.config.linkedLinks.length; i++) {
      let j = node.config.linkedLinks[i],
        obj = manager.config.links[j],
        arrValues = [],
        currentnodelabel = eventArgs.label,
        currentnodevalue = {},
        linkednodelabel,
        linkednodevalue = {};
      currentnodevalue[currentnodelabel] = obj[currentnodelabel];
      linkednodelabel = (j.replace(currentnodelabel, '')).replace(',', '');
      linkednodevalue[linkednodelabel] = obj[linkednodelabel];
      arrValues.push(currentnodevalue);
      if (linkednodelabel !== currentnodelabel && !(obj[linkednodelabel] === UNDEF)) { arrValues.push(linkednodevalue); }
      eventArgs.links[j] = arrValues;
    }
  },
  getTooltext = (config, node, chartConfig) => {
    let uniChar = isIpad ? SMALLSQUARE : MEDIUMSQUARE,
      macroIndices = [3, 133, 134, 143, 144, 145],
      basefontsize = node.computeFontSize(
        chartConfig.style.inCanfontSizeWithUnit),
      manager = node.getLinkedParent(),
      drawCustomLegendIcon = chartConfig && chartConfig.drawcustomlegendicon,
      legend = node.getFromEnv('legend'),
      pathArr,
      newPathArr = [],
      i,
      j,
      symbolBBox,
      legendItemSymbol,
      plotToolText,
      pathStart = [],
      finalPath,
      legendItem = (drawCustomLegendIcon && legend && legend.config && legend.config.isActive) ? legend.getItem(manager.config.legendItemMap[node.config.label]) : null,
      graphicalElement = legendItem && legendItem.getGraphicalElement(),
      parserConfig,
      toolText;
    finalPath = BLANKSTRING;
    if (legendItem) {
      legendItemSymbol = graphicalElement && graphicalElement.legendItemSymbol;
      pathArr = legendItemSymbol && legendItemSymbol.attrs.path.split(/(?=[LMCA])/);
      symbolBBox = legendItemSymbol && legendItemSymbol.node.getBBox();
      if (pathArr && pathArr.length) {
        for (i = 0; i < pathArr.length; i++) {
          pathStart.push(pathArr[i][0]);
          pathArr[i] = pathArr[i].substring(1, pathArr[i].length);
          if (i === (pathArr.length - 1)) {
            pathArr[i] = pathArr[i].substring(0, pathArr[i].length - 1);
          }
          newPathArr[i] = pathArr[i].split(',');
        }
      }
      if (newPathArr && newPathArr.length) {
        for (i = 0; i < newPathArr.length; i++) {
          if (newPathArr[i].length) {
            for (j = 0; j < newPathArr[i].length; j++) {
              newPathArr[i][j] = Number(newPathArr[i][j]);
              if (pathStart[i] !== 'A') {
                if (j === 0) {
                  newPathArr[i][j] = newPathArr[i][j] - (symbolBBox.x);
                } else if (j === 1) {
                  newPathArr[i][j] = newPathArr[i][j] - (symbolBBox.y);
                }
              } else if (pathStart[i] === 'A') {
                if (j === 5) {
                  newPathArr[i][j] = newPathArr[i][j] - (symbolBBox.x);
                } else if (j === 6) {
                  newPathArr[i][j] = newPathArr[i][j] - (symbolBBox.y);
                }
              }
            }
          }
          finalPath += pathStart[i] + newPathArr[i].toString();
        }
      }
      finalPath = finalPath + 'Z';
    }
    parserConfig = {
      label: config.label,
      value: config.total,
      nodeValue: config.total,
      nodeDataValue: config.formattedValue,
      plotFillColor: config.color,
      plotFillAlpha: config.alpha,
      plotIdentifier: legendItem ? `<svg height= ${symbolBBox.height} width = ${symbolBBox.width} overflow='visible' xmlns="http://www.w3.org/2000/svg">
      <path d= ${finalPath} fill = ${graphicalElement.legendItemSymbol.node.getAttribute('fill')} stroke = ${graphicalElement.legendItemSymbol.node.getAttribute('stroke')}
      stroke-opacity = ${graphicalElement.legendItemSymbol.node.getAttribute('stroke-opacity')} fill-opacity = ${graphicalElement.legendItemSymbol.node.getAttribute('fill-opacity')} 
      stroke-width = ${graphicalElement.legendItemSymbol.node.getAttribute('stroke-width')} stroke-linecap = ${graphicalElement.legendItemSymbol.node.getAttribute('stroke-linecap')}></path>
    </svg>` : `<span style='color: ${config.color};'>${uniChar}&nbsp;</span>`
    };

    toolText = parseTooltext(config.tooltext, macroIndices,
      parserConfig, {
        label: config.label,
        value: config.formattedValue,
        nodeValue: config.total,
        nodeDataValue: config.formattedValue,
        plotFillColor: config.color,
        plotFillAlpha: config.alpha,
        plotIdentifier: legendItem ? `<svg height= ${symbolBBox.height} width = ${symbolBBox.width} overflow='visible' xmlns="http://www.w3.org/2000/svg">
        <path d= ${finalPath} fill = ${graphicalElement.legendItemSymbol.node.getAttribute('fill')} stroke = ${graphicalElement.legendItemSymbol.node.getAttribute('stroke')}
        stroke-opacity = ${graphicalElement.legendItemSymbol.node.getAttribute('stroke-opacity')} fill-opacity = ${graphicalElement.legendItemSymbol.node.getAttribute('fill-opacity')} 
        stroke-width = ${graphicalElement.legendItemSymbol.node.getAttribute('stroke-width')} stroke-linecap = ${graphicalElement.legendItemSymbol.node.getAttribute('stroke-linecap')}></path>
      </svg>` : `<span style='color: ${config.color};'>${uniChar}&nbsp;</span>`
      }
    );

    plotToolText = parseTooltext(config.tooltext, macroIndices,
      parserConfig, {
        label: config.label,
        value: config.formattedValue,
        nodeValue: config.total,
        nodeDataValue: config.formattedValue,
        plotFillColor: config.color,
        plotFillAlpha: config.alpha,
        plotIdentifier: legendItem ? `<svg height= ${symbolBBox.height} width = ${symbolBBox.width} overflow='visible' xmlns="http://www.w3.org/2000/svg">
        <path d= ${finalPath} fill = ${graphicalElement.legendItemSymbol.node.getAttribute('fill')} stroke = ${graphicalElement.legendItemSymbol.node.getAttribute('stroke')}
        stroke-opacity = ${graphicalElement.legendItemSymbol.node.getAttribute('stroke-opacity')} fill-opacity = ${graphicalElement.legendItemSymbol.node.getAttribute('fill-opacity')} 
        stroke-width = ${graphicalElement.legendItemSymbol.node.getAttribute('stroke-width')} stroke-linecap = ${graphicalElement.legendItemSymbol.node.getAttribute('stroke-linecap')}></path>
      </svg>` : `<span style='color: ${config.color};'>${uniChar}&nbsp;</span>`
      }
    );

    config.setToolText = toolText;
    config.setPlotTooltext = config.setToolText ? config.setToolText : plotToolText;

    if (!config.tooltext) {
      return (
        `<div style='padding: 2px; vertical-align: middle; font-size: ${basefontsize}px;'>
          <span style='color: ${config.color};'>
              ${uniChar}
          </span>
          ${config.label + config.toolTipSepChar}
          &nbsp;
          ${config.formattedValue}
        </div>`
      );
    }
    return (
      `<div style='padding: 2px; vertical-align: middle; font-size: ${basefontsize}px;'>
        <span style='color: ${config.color};'>
            ${uniChar}
        </span>
        ${config.setPlotTooltext}
      </div>`
    );
  };

class Node extends SmartRenderer {
  constructor (id) {
    super(id);
    this.addEventListener('fc-click', clickFn);
    // this.addEventListener('fc-click', dataPlotClickfn);
    this.addEventListener('fc-mouseover', mouseOverFn);
    this.addEventListener('fc-mouseout', mouseOutFn);
  }
  __setDefaultConfig () {
    super.__setDefaultConfig();
    const config = this.config;
    config.styles = {
      fill: RED,
      stroke: RED,
      'fill-alpha': 0.7
    };
  }

  configureAttributes (dataObj) {
    super.configureAttributes(dataObj);
    Object.assign(this.config, dataObj);
    const node = this;
    node.config.formattedValue = node.getFromEnv('number-formatter').dataLabels(node.config.total);
    node.config.formattedNodeDataValue = node.getFromEnv('number-formatter').dataLabels(node.config.nodedatavalue);
  }

  setDimension (dim) {
    Object.assign(this.config, dim);
  }

  allocatePosition () {
    let node = this,
      config = node.config,
      label = config.label,
      { canvasHeight, canvasWidth, cx, cy, style } = node.getFromEnv('chartConfig'),
      slope,
      labelOrientation = config.labelPosition,
      applicableRadius = config.outerRadius + config.nodeLabelGap,
      smartLabel = node.getFromEnv('smartLabel'),
      startingAngle = normaliseAngle(config.startingAngle),
      endingAngle = normaliseAngle(config.endingAngle),
      labelAttrs = {},
      smartLabelOutput,
      fontSize,
      lineHeight,
      labelCatersianPosition;

    // Setting font size & line height for when the chart is resized
    config.style['font-size'] = fontSize = this.computeFontSize(style.inCanfontSizeWithUnit);
    lineHeight = setLineHeight({ fontSize: fontSize }).replace(/px/ig, BLANKSTRING);
    smartLabel.setStyle(config.style);
    smartLabel.useEllipsesOnOverflow(node.getFromEnv('chartConfig').useEllipsesOnOverflow);
    /*
      * RED-8797
      * SVG cartesian system is not same as general cartesian system
      * But since slope is calculated down, it must be corrected as per
      * general cartesian system.
      * Followed by that if-else conditions are also matched with general
      * cartesian system.
      * Note: CASE 0, 1, 2, 3 still map to SVG cartesian system.
    */
    switch (labelOrientation) {
      case LABEL_POSITION_TANGENTIAL: {
        let labelAngleCenter = ((startingAngle + endingAngle) / 2) % 360,
          x2, y2,
          spaceRemaining = 0;

        labelCatersianPosition = polarToCartesian(applicableRadius, labelAngleCenter, false);
        const quadrant = getQuadrant(deg2Rad(labelAngleCenter));
        switch (quadrant) {
          case 0:
            x2 = canvasWidth / 2;
            y2 = canvasHeight / 2;
            slope = Math.tan(deg2Rad(360 - labelAngleCenter));
            if (Math.abs(x2 * slope) <= y2) {
              // intersects with right line
              spaceRemaining = getCartesianDistance(labelCatersianPosition.x + cx, labelCatersianPosition.y + cy, cx + x2, cy - x2 * slope);
            } else {
              // intersects with bottom line
              spaceRemaining = getCartesianDistance(labelCatersianPosition.x + cx, labelCatersianPosition.y + cy, cx - (y2 / slope), cy + y2);
            }
            break;

          case 1:
            x2 = (-1 * canvasWidth / 2);
            y2 = (canvasHeight / 2);
            slope = Math.tan(deg2Rad(180 + labelAngleCenter));

            if (Math.abs(x2 * slope) <= Math.abs(y2)) {
              // intersects with left line
              spaceRemaining = getCartesianDistance(labelCatersianPosition.x + cx, labelCatersianPosition.y + cy, cx + x2, cy + x2 * slope);
            } else {
              // intersects with bottom line
              spaceRemaining = getCartesianDistance(labelCatersianPosition.x + cx, labelCatersianPosition.y + cy, cx + (y2 / slope), cy + y2);
            }
            break;

          case 2:
            x2 = (-1 * canvasWidth / 2);
            y2 = -1 * (canvasHeight / 2);
            slope = Math.tan(deg2Rad(360 - labelAngleCenter));

            if (Math.abs(x2 * slope) <= Math.abs(y2)) {
              // intersects with left line
              spaceRemaining = getCartesianDistance(labelCatersianPosition.x + cx, labelCatersianPosition.y + cy, cx + x2, cy - x2 * slope);
            } else {
              // intersects with top line
              spaceRemaining = getCartesianDistance(labelCatersianPosition.x + cx, labelCatersianPosition.y + cy, cx - (y2 / slope), cy + y2);
            }
            break;

          case 3:
            x2 = canvasWidth / 2;
            y2 = -1 * (canvasHeight / 2);
            slope = Math.tan(deg2Rad(-1 * labelAngleCenter));

            if (Math.abs(x2 * slope) <= Math.abs(y2)) {
              // intersects with right line
              spaceRemaining = getCartesianDistance(labelCatersianPosition.x + cx, labelCatersianPosition.y + cy, cx + x2, cy - x2 * slope);
            } else {
              // intersects with top line
              spaceRemaining = getCartesianDistance(labelCatersianPosition.x + cx, labelCatersianPosition.y + cy, cx - (y2 / slope), cy + y2);
            }
            break;

          default: ;
        }

        smartLabelOutput = smartLabel.getSmartText(label, spaceRemaining, lineHeight);
        labelAttrs.transform = getSuggestiveRotation(
          getTextRotationByQuadrant(labelAngleCenter),
          labelCatersianPosition.x, labelCatersianPosition.y);

        if (isAngleInLeftHemisphere(labelAngleCenter)) {
          labelAttrs['text-anchor'] = END;
        }

        labelAttrs.x = labelCatersianPosition.x;
        labelAttrs.y = labelCatersianPosition.y;
        break;
      }
      case LABEL_POSITION_OUTSIDE: {
        let labelAngleCenterDeg = ((startingAngle + endingAngle) / 2) % 360,
          labelAngleCenterRad = deg2Rad(labelAngleCenterDeg),
          labelAngleCenterCleanRad = getCleanAngle(labelAngleCenterRad),
          labelAngleCenterCleanDeg = rad2Deg(labelAngleCenterCleanRad),
          isLabelCenterInTopHemisphere = isAngleInTopHemisphere(labelAngleCenterCleanDeg),
          arcLength = getArcLength(
            applicableRadius,
            startingAngle,
            endingAngle
          ),
          textPathStartAngle = isLabelCenterInTopHemisphere
            ? config.startingAngle
            : config.endingAngle,
          textPathEndAngle = isLabelCenterInTopHemisphere
            ? config.endingAngle
            : config.startingAngle;
        smartLabelOutput = smartLabel.getSmartText(label, arcLength - PADDING, lineHeight);
        // dy attribute of tspan not supported in IE, so path calculation changed
        if (isIE11) {
          if (!isLabelCenterInTopHemisphere) {
            applicableRadius += smartLabelOutput.height * 0.7;
          }
        }

        labelAttrs.textpath = {
          path: labelPathGenerator(
            textPathStartAngle,
            textPathEndAngle,
            config.arcAngle,
            applicableRadius
          ),
          startOffset: '50%'
        };

        labelAttrs[TEXTANCHOR] = MIDDLESTR;
        labelAttrs['vertical-align'] = isLabelCenterInTopHemisphere
          ? 'top'
          : 'bottom';

        labelAttrs.transform = null;
        labelAttrs.x = 0;
        labelAttrs.y = 0;
        break;
      }
      case LABEL_POSITION_INSIDE:
      default: {
        let labelAngleCenterDeg = ((startingAngle + endingAngle) / 2) % 360,
          labelAngleCenterRad = deg2Rad(labelAngleCenterDeg),
          labelAngleCenterCleanRad = getCleanAngle(labelAngleCenterRad),
          labelAngleCenterCleanDeg = rad2Deg(labelAngleCenterCleanRad),
          isLabelCenterInTopHemisphere = isAngleInTopHemisphere(labelAngleCenterCleanDeg),
          middleRadius = (config.outerRadius + config.innerRadius) / 2,
          arcLength = getArcLength(
            middleRadius,
            startingAngle,
            endingAngle
          ),
          textPathStartAngle = isLabelCenterInTopHemisphere
            ? config.startingAngle
            : config.endingAngle,
          textPathEndAngle = isLabelCenterInTopHemisphere
            ? config.endingAngle
            : config.startingAngle;

        smartLabelOutput = smartLabel.getSmartText(label, arcLength - PADDING, lineHeight);
        // IE doesn't support dy attribute of tspan, so path calculation changed
        if (isIE11) {
          if (isLabelCenterInTopHemisphere) {
            middleRadius -= smartLabelOutput.height * 0.3;
          } else {
            middleRadius += smartLabelOutput.height * 0.3;
          }
        }
        labelAttrs.textpath = {
          path: labelPathGenerator(
            textPathStartAngle,
            textPathEndAngle,
            config.arcAngle,
            middleRadius
          ),
          startOffset: '50%'
        };

        labelAttrs[TEXTANCHOR] = MIDDLESTR;
        labelAttrs['vertical-align'] = MIDDLESTR;

        labelAttrs.transform = null;
        labelAttrs.x = 0;
        labelAttrs.y = 0;
        break;
      }
    }

    labelAttrs.fill = convertColor(config.labelColor, 100);
    labelAttrs.text = smartLabelOutput.text;
    labelAttrs.cursor = POINTER;
    labelAttrs['text-anchor'] = labelAttrs['text-anchor'] || START;
    node.config.labelAttrs = labelAttrs;
    node.config.path = nodeGenerator(config.startingAngle, config.endingAngle, config.arcAngle,
      config.outerRadius, config.innerRadius);
    node.config.focussedState.path = node.config.path;
    node.config.normalState.path = node.config.path;
    node.config.unfocussedState.path = node.config.path;
    node.config.deactiveState.path = node.config.path;
  }

  draw () {
    const node = this,
      config = node.config,
      chartConfig = node.getFromEnv('chartConfig'),
      tooltext = config.showToolTip ? getTooltext(config, this, chartConfig) : BLANKSTRING;
    let nodeAttr;

    if (node.config.active) {
      nodeAttr = config.hovered ? config.focussedState : config.unfocussed ? config.unfocussedState : config.normalState;
    } else {
      nodeAttr = config.deactiveState;
    }

    nodeAttr.cursor = POINTER;

    node.addGraphicalElement({
      el: 'group',
      container: {
        id: 'node-container',
        isParent: true
      },
      tooltext,
      component: node,
      label: 'text-path-container',
      id: 'text-path-container',
      attr: {
        name: 'text-path-container'
      }
    });

    node.addGraphicalElement({
      el: 'path',
      label: 'node',
      tooltext,
      container: {
        id: 'text-path-container'
      },
      attr: nodeAttr,
      component: node
    }, true);

    if (config.showLabel) {
      node.addGraphicalElement({
        el: 'text',
        label: 'node-label',
        container: {
          id: 'text-path-container'
        },
        attr: config.labelAttrs,
        outlineText: !!chartConfig.textOutline,
        css: {},
        tooltext,
        component: node
      }, true);
    }
  }

  _manageSpace (spaceArguments) {
    const node = this,
      config = node.config,
      smartLabel = node.getFromEnv('smartLabel');

    let oriSize,
      smartText,
      spaceRequired;

    smartLabel.setStyle(config.style);
    oriSize = smartLabel.getOriSize(config.label);

    if (!config.showLabel) {
      return 0;
    }

    if (config.labelPosition === LABEL_POSITION_TANGENTIAL) {
      if (oriSize.width > spaceArguments.maxSpace) {
        smartText = smartLabel.getSmartText(config.label, spaceArguments.maxSpace, oriSize.height);
        spaceRequired = smartText.width;
      } else {
        spaceRequired = oriSize.width;
      }
    } else if (config.labelPosition === LABEL_POSITION_OUTSIDE) {
      spaceRequired = oriSize.height;
    } else {
      spaceRequired = 0;
    }

    return spaceRequired;
  }

  getName () {
    return 'node';
  }

  getType () {
    return 'dataset';
  }
}

export default Node;
